import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BusinessRoleView,
  BusinessRoleType,
  RoleAssignmentView,
  RoleAssignmentStatus,
  EmployeeSimpleView,
  getRoleAssignments,
  searchEmployeesForAssignment,
  assignRole,
  unassignRole,
  detectConflicts,
  RoleConflictView,
} from '../../../api/General/businessRoles';
import RoleConflictModal from './RoleConflictModal';
import Button from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';

interface RoleAssignmentPanelProps {
  role: BusinessRoleView;
  onAssignmentChanged: () => void;
}

const roleTypeStyles: Record<BusinessRoleType, string> = {
  [BusinessRoleType.System]: 'bg-primary/10 text-primary',
  [BusinessRoleType.Project]: 'bg-purple-100 text-purple-700',
  [BusinessRoleType.Temporary]: 'bg-orange-100 text-orange-700',
};

const assignmentStatusStyles: Record<RoleAssignmentStatus, string> = {
  [RoleAssignmentStatus.Active]: 'bg-success/10 text-success',
  [RoleAssignmentStatus.Expired]: 'bg-muted text-muted-foreground',
  [RoleAssignmentStatus.Revoked]: 'bg-destructive/10 text-destructive',
};

const assignmentStatusLabels: Record<RoleAssignmentStatus, string> = {
  [RoleAssignmentStatus.Active]: 'Active',
  [RoleAssignmentStatus.Expired]: 'Expired',
  [RoleAssignmentStatus.Revoked]: 'Revoked',
};

export default function RoleAssignmentPanel({ role, onAssignmentChanged }: RoleAssignmentPanelProps) {
  const { t } = useTranslation();

  const [assignments, setAssignments] = useState<RoleAssignmentView[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAssignForm, setShowAssignForm] = useState(false);

  // Assign form state
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [employees, setEmployees] = useState<EmployeeSimpleView[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSimpleView | null>(null);
  const [effectiveDate, setEffectiveDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Conflict modal
  const [conflicts, setConflicts] = useState<RoleConflictView[]>([]);
  const [showConflictModal, setShowConflictModal] = useState(false);

  // Unassign
  const [unassigning, setUnassigning] = useState<number | null>(null);

  const loadAssignments = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRoleAssignments(role.id);
      if (result.errors.length === 0) {
        setAssignments(result.payload.items);
      }
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [role.id]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced employee search
  useEffect(() => {
    if (!employeeSearch || employeeSearch.length < 2) {
      setEmployees([]);
      setShowDropdown(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const result = await searchEmployeesForAssignment(employeeSearch);
        if (result.errors.length === 0) {
          setEmployees(result.payload);
          setShowDropdown(true);
        }
      } catch { /* silent */ }
    }, 300);

    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [employeeSearch]);

  const handleSelectEmployee = (emp: EmployeeSimpleView) => {
    setSelectedEmployee(emp);
    setEmployeeSearch(emp.fullName);
    setShowDropdown(false);
  };

  const resetForm = () => {
    setSelectedEmployee(null);
    setEmployeeSearch('');
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    setExpiryDate('');
    setAssignNotes('');
    setShowAssignForm(false);
  };

  const handleAssign = async (forceAssign = false) => {
    if (!selectedEmployee) return;

    setAssigning(true);
    try {
      // First check for conflicts (unless force)
      if (!forceAssign) {
        const conflictResult = await detectConflicts(role.id, selectedEmployee.id);
        if (conflictResult.errors.length === 0 && conflictResult.payload.hasConflicts) {
          setConflicts(conflictResult.payload.conflicts);
          setShowConflictModal(true);
          setAssigning(false);
          return;
        }
      }

      const result = await assignRole({
        businessRoleId: role.id,
        employeeId: selectedEmployee.id,
        effectiveDate,
        expiryDate: expiryDate || undefined,
        notes: assignNotes || undefined,
        forceAssign,
      });

      if (result.errors.length === 0) {
        resetForm();
        loadAssignments();
        onAssignmentChanged();
      }
    } catch { /* silent */ } finally {
      setAssigning(false);
    }
  };

  const handleConflictProceed = () => {
    setShowConflictModal(false);
    handleAssign(true);
  };

  const handleUnassign = async (assignmentId: number) => {
    if (!window.confirm(t('businessRoles.confirmUnassign'))) return;
    setUnassigning(assignmentId);
    try {
      const result = await unassignRole({ assignmentId });
      if (result.errors.length === 0) {
        loadAssignments();
        onAssignmentChanged();
      }
    } catch { /* silent */ } finally {
      setUnassigning(null);
    }
  };

  const activeAssignments = assignments.filter(a => a.status === RoleAssignmentStatus.Active);
  const pastAssignments = assignments.filter(a => a.status !== RoleAssignmentStatus.Active);
  const seatsAvailable = role.numberOfSeats - activeAssignments.length;

  return (
    <div className="mt-6 pt-6 border-t border-border">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground">{t('businessRoles.assignments')}</h3>
        {seatsAvailable > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAssignForm(!showAssignForm)}
            iconName="UserPlus"
          >
            {t('businessRoles.assignEmployee')}
          </Button>
        )}
      </div>

      {/* Assign Form */}
      {showAssignForm && (
        <div className="bg-muted rounded-lg p-4 mb-4 border border-border">
          <div className="space-y-3">
            {/* Employee search */}
            <div className="relative" ref={dropdownRef}>
              <label className="block text-xs font-medium text-foreground mb-1">{t('businessRoles.selectEmployee')}</label>
              <input
                type="text"
                value={employeeSearch}
                onChange={(e) => {
                  setEmployeeSearch(e.target.value);
                  if (selectedEmployee && e.target.value !== selectedEmployee.fullName) {
                    setSelectedEmployee(null);
                  }
                }}
                placeholder={t('businessRoles.searchEmployee')}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
              {selectedEmployee && (
                <div className="absolute end-2 top-7 flex items-center">
                  <svg className="w-4 h-4 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              )}
              {showDropdown && employees.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {employees.map((emp) => (
                    <button
                      key={emp.id}
                      onClick={() => handleSelectEmployee(emp)}
                      className="w-full text-left px-3 py-2 hover:bg-muted flex items-center gap-2"
                    >
                      <div className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-primary">
                          {emp.fullName.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-foreground truncate">{emp.fullName}</p>
                        {emp.department && (
                          <p className="text-xs text-muted-foreground">{emp.department}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">{t('businessRoles.effectiveDate')}</label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">
                  {t('businessRoles.expiryDate')}
                  <span className="text-muted-foreground font-normal ms-1">({t('common.optional')})</span>
                </label>
                <input
                  type="date"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  min={effectiveDate}
                  className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs font-medium text-foreground mb-1">
                {t('businessRoles.assignmentNotes')}
                <span className="text-muted-foreground font-normal ms-1">({t('common.optional')})</span>
              </label>
              <input
                type="text"
                value={assignNotes}
                onChange={(e) => setAssignNotes(e.target.value)}
                placeholder={t('businessRoles.assignmentNotesPlaceholder')}
                className="w-full px-3 py-2 border border-border rounded-lg text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={resetForm}>
                {t('common.cancel')}
              </Button>
              <Button
                size="sm"
                onClick={() => handleAssign(false)}
                disabled={!selectedEmployee || assigning}
                loading={assigning}
              >
                {t('businessRoles.assign')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Seat indicator */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              seatsAvailable <= 0 ? 'bg-destructive' : seatsAvailable <= 1 ? 'bg-warning' : 'bg-success'
            }`}
            style={{ width: `${Math.min(100, (activeAssignments.length / role.numberOfSeats) * 100)}%` }}
          />
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          {activeAssignments.length}/{role.numberOfSeats} {t('businessRoles.seatsUsed')}
        </span>
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Spinner size="sm" />
        </div>
      ) : assignments.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground">{t('businessRoles.noAssignments')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Active assignments */}
          {activeAssignments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase mb-2">{t('businessRoles.currentAssignees')}</p>
              <div className="space-y-2">
                {activeAssignments.map((a) => {
                  const isExpiringSoon = a.expiryDate && new Date(a.expiryDate) <= new Date(Date.now() + 14 * 24 * 60 * 60 * 1000);
                  return (
                    <div key={a.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                      isExpiringSoon ? 'border-warning/20 bg-warning/5' : 'border-border bg-card'
                    }`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary">
                            {(a.employeeName || '?').charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{a.employeeName || a.employeeId}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{new Date(a.effectiveDate).toLocaleDateString()}</span>
                            {a.expiryDate && (
                              <>
                                <span>-</span>
                                <span className={isExpiringSoon ? 'text-warning font-medium' : ''}>
                                  {new Date(a.expiryDate).toLocaleDateString()}
                                  {isExpiringSoon && ` (${t('businessRoles.expiringSoon')})`}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/5"
                        onClick={() => handleUnassign(a.id)}
                        disabled={unassigning === a.id}
                      >
                        {unassigning === a.id ? '...' : t('businessRoles.unassign')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Past assignments */}
          {pastAssignments.length > 0 && (
            <div>
              <p className="text-xs font-medium text-muted-foreground uppercase mb-2">{t('businessRoles.pastAssignments')}</p>
              <div className="space-y-1.5">
                {pastAssignments.map((a) => (
                  <div key={a.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border bg-muted">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="w-7 h-7 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-medium text-muted-foreground">
                          {(a.employeeName || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-medium text-foreground truncate">{a.employeeName || a.employeeId}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(a.effectiveDate).toLocaleDateString()}
                          {a.expiryDate && ` - ${new Date(a.expiryDate).toLocaleDateString()}`}
                        </p>
                      </div>
                    </div>
                    <span className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${assignmentStatusStyles[a.status]}`}>
                      {t(`businessRoles.assignment${assignmentStatusLabels[a.status]}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Conflict Modal */}
      <RoleConflictModal
        open={showConflictModal}
        conflicts={conflicts}
        onProceed={handleConflictProceed}
        onCancel={() => setShowConflictModal(false)}
      />
    </div>
  );
}
